import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  TextField,
  Typography
} from "@material-ui/core";
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  WarningRounded as WarningIcon
} from "@material-ui/icons";
import { DebounceInput } from "react-debounce-input";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import Dialog from "../../waybee-ui/Dialog";
import DialogTitle from "../../waybee-ui/Dialog/DialogTitle";
import DialogContent from "../../waybee-ui/Dialog/DialogContent";
import DialogActions from "../../waybee-ui/Dialog/DialogActions";
import Button from "../../waybee-ui/Button";
import TeacherService from "../../services/TeacherService";
import ClassService from "../../services/ClassService";
import {
  FolderBlock,
  FolderLabel,
  LeafLabel,
  LeafRow,
  NodeRow,
  SectionHeader,
  SectionHint,
  SectionTitle,
  TreePanel,
  TreeWrap
} from "../../components/CheckboxTree/CheckboxTreeStyles";

const DialogSubtitle = styled(Typography)`
  && {
    color: #6b6b6b;
    font-size: 14px;
    margin: -8px 0 20px;
  }
`;

const SeriesFilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
`;

const FilterLabel = styled(Typography)`
  && {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    color: #777;
  }
`;

const LinksRequiredWarning = styled(Typography)`
  && {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 4px 0 8px;
    padding: 8px 12px;
    border-radius: 4px;
    background-color: #fff4e5;
    color: #8a5300;
    font-size: 13px;
  }
`;

const PanelFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 4px 4px;
`;

const toClassPage = response => {
  const items =
    (response && response.items) || (Array.isArray(response) ? response : []);
  const total =
    response && response.total != null ? Number(response.total) : items.length;
  return { items, total };
};

const withDisciplines = async item => {
  const id = String(item.id);
  const name = item.name || id;
  const series = item.series || null;
  try {
    const disciplines = await ClassService.getDisciplines(id);
    return {
      id,
      name,
      series,
      disciplines: disciplines || [],
      hasError: false
    };
  } catch (e) {
    return { id, name, series, disciplines: [], hasError: true };
  }
};

const fetchClassPage = async ({ search, skip, series }) => {
  const { items, total } = toClassPage(
    await TeacherService.getClasses({
      search: search || undefined,
      limit: 10,
      skip,
      series: series || undefined,
      searchDisciplines: true
    })
  );
  return { groups: await Promise.all(items.map(withDisciplines)), total };
};

const fetchCurrentClassGroup = async classId => {
  if (!classId) return null;
  const { items } = toClassPage(
    await TeacherService.getClasses({
      search: classId,
      limit: 10
    })
  );
  const current = items.find(item => String(item.id) === String(classId));
  return current ? withDisciplines(current) : null;
};

const matchingDisciplines = (group, search) => {
  const term = search.trim().toLowerCase();
  if (!term || group.name.toLowerCase().includes(term))
    return group.disciplines;
  const matching = group.disciplines.filter(discipline =>
    (discipline.name || "").toLowerCase().includes(term)
  );
  return matching.length ? matching : group.disciplines;
};

const ClassFolder = ({ group, search, selectedIds, onToggle }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const toggle = () => setOpen(previous => !previous);

  return (
    <FolderBlock>
      <NodeRow $clickable onClick={toggle} role="button">
        <IconButton
          size="small"
          onClick={event => {
            event.stopPropagation();
            toggle();
          }}
        >
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
        <FolderLabel>{group.name}</FolderLabel>
      </NodeRow>
      <Collapse in={open}>
        {group.hasError && (
          <Box ml={4}>
            <SectionHint>
              {t("classStages.assignment.disciplinesLoadError")}
            </SectionHint>
          </Box>
        )}
        {!group.hasError && !group.disciplines.length && (
          <Box ml={4}>
            <SectionHint>
              {t("classStages.assignment.noDisciplines")}
            </SectionHint>
          </Box>
        )}
        {matchingDisciplines(group, search).map(discipline => {
          const id = String(discipline.idTurmaDisc);
          const checked = selectedIds.has(id);
          return (
            <LeafRow key={id} $checked={checked}>
              <FormControlLabel
                style={{ margin: 0, width: "100%" }}
                control={(
                  <Checkbox
                    color="primary"
                    size="small"
                    checked={checked}
                    onChange={() => onToggle(id)}
                  />
                )}
                label={<LeafLabel>{discipline.name || id}</LeafLabel>}
              />
            </LeafRow>
          );
        })}
      </Collapse>
    </FolderBlock>
  );
};

ClassFolder.propTypes = {
  group: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    disciplines: PropTypes.arrayOf(PropTypes.shape({})),
    hasError: PropTypes.bool
  }).isRequired,
  search: PropTypes.string.isRequired,
  selectedIds: PropTypes.instanceOf(Set).isRequired,
  onToggle: PropTypes.func.isRequired
};

const StageAssignmentDialog = ({
  open,
  onClose,
  onConfirm,
  stageTitle,
  currentClassId,
  initialLinks
}) => {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const [pinnedGroup, setPinnedGroup] = useState(null);
  const [groups, setGroups] = useState([]);
  const [fetchedCount, setFetchedCount] = useState(0);
  const [totalClasses, setTotalClasses] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const [currentClassLoaded, setCurrentClassLoaded] = useState(false);
  const [sameSeriesOnly, setSameSeriesOnly] = useState(true);
  const requestSeq = useRef(0);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setSearchText("");
    setConfirming(false);
    setSelectedIds(new Set((initialLinks || []).map(link => link.idTurmaDisc)));
  }, [open, initialLinks]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    setCurrentClassLoaded(false);
    setSameSeriesOnly(true);
    fetchCurrentClassGroup(currentClassId)
      .catch(() => null)
      .then(current => {
        if (!active) return;
        setPinnedGroup(current);
        setCurrentClassLoaded(true);
      });
    return () => {
      active = false;
    };
  }, [open, currentClassId]);

  const currentSeries = pinnedGroup ? pinnedGroup.series : null;
  const seriesFilter = sameSeriesOnly ? currentSeries : null;

  useEffect(() => {
    if (!open || !currentClassLoaded) return;
    requestSeq.current += 1;
    const seq = requestSeq.current;
    setLoading(true);
    setHasError(false);
    setLoadMoreFailed(false);

    fetchClassPage({ search: searchText.trim(), skip: 0, series: seriesFilter })
      .then(page => {
        if (seq !== requestSeq.current) return;
        setGroups(page.groups);
        setFetchedCount(page.groups.length);
        setTotalClasses(page.total);
      })
      .catch(() => {
        if (seq === requestSeq.current) setHasError(true);
      })
      .finally(() => {
        if (seq === requestSeq.current) setLoading(false);
      });
  }, [open, searchText, currentClassLoaded, seriesFilter]);

  const visibleGroups = useMemo(() => {
    if (!pinnedGroup || searchText.trim()) return groups;
    return [pinnedGroup].concat(
      groups.filter(group => group.id !== pinnedGroup.id)
    );
  }, [pinnedGroup, groups, searchText]);

  const availableIds = useMemo(
    () =>
      visibleGroups.flatMap(group =>
        group.disciplines.map(discipline => String(discipline.idTurmaDisc))
      ),
    [visibleGroups]
  );

  const linksById = useMemo(() => {
    const links = new Map(
      (initialLinks || []).map(link => [link.idTurmaDisc, link])
    );
    visibleGroups.forEach(group =>
      group.disciplines.forEach(discipline => {
        const idTurmaDisc = String(discipline.idTurmaDisc);
        links.set(idTurmaDisc, {
          idTurmaDisc,
          classId: group.id,
          className: group.name,
          disciplineName: discipline.name || idTurmaDisc
        });
      })
    );
    return links;
  }, [initialLinks, visibleGroups]);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await onConfirm(
        [...selectedIds].map(id => linksById.get(id)).filter(Boolean)
      );
    } finally {
      setConfirming(false);
    }
  };

  const hasMore = fetchedCount < totalClasses;

  const loadMore = () => {
    if (loading || loadingMore || !hasMore) return;
    const seq = requestSeq.current;
    setLoadingMore(true);
    setLoadMoreFailed(false);
    fetchClassPage({
      search: searchText.trim(),
      skip: fetchedCount,
      series: seriesFilter
    })
      .then(page => {
        if (seq !== requestSeq.current) return;
        setGroups(previous => previous.concat(page.groups));
        setFetchedCount(previous => previous + page.groups.length);
        setTotalClasses(page.total);
      })
      .catch(() => {
        if (seq === requestSeq.current) setLoadMoreFailed(true);
      })
      .finally(() => setLoadingMore(false));
  };

  const handlePanelScroll = event => {
    const panel = event.currentTarget;
    if (panel.scrollHeight - panel.scrollTop - panel.clientHeight < 48) {
      loadMore();
    }
  };

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel || loadMoreFailed) return;
    if (panel.scrollHeight <= panel.clientHeight) loadMore();
  });

  const toggleDiscipline = id => {
    setSelectedIds(previous => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const renderTree = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      );
    }
    if (hasError) {
      return (
        <Typography color="textSecondary">
          {t("classStages.assignment.classesLoadError")}
        </Typography>
      );
    }
    if (!visibleGroups.length) {
      return (
        <Typography color="textSecondary">
          {searchText.trim()
            ? t("classStages.assignment.emptySearch")
            : t("classStages.assignment.noClasses")}
        </Typography>
      );
    }
    return (
      <>
        {visibleGroups.map(group => (
          <ClassFolder
            key={group.id}
            group={group}
            search={searchText}
            selectedIds={selectedIds}
            onToggle={toggleDiscipline}
          />
        ))}
        <PanelFooter>
          <SectionHint>
            {t("classStages.assignment.classesShown", {
              shown: fetchedCount,
              total: totalClasses
            })}
          </SectionHint>
          {loadingMore && <CircularProgress size={16} />}
          {loadMoreFailed && (
            <SectionHint>
              {t("classStages.assignment.loadMoreError")}
            </SectionHint>
          )}
        </PanelFooter>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      closeButton
      closeButtonPosition={{ top: "-14px", right: "-14px" }}
      PaperProps={{ style: { overflow: "visible" } }}
    >
      <DialogTitle>{t("classStages.assignment.title")}</DialogTitle>
      <DialogContent>
        {stageTitle && <DialogSubtitle>{stageTitle}</DialogSubtitle>}
        <SectionHeader>
          <div>
            <SectionTitle>
              {t("classStages.assignment.sectionTitle")}
            </SectionTitle>
            <SectionHint>
              {t("classStages.assignment.selectedCount", {
                selected: availableIds.filter(id => selectedIds.has(id)).length,
                total: availableIds.length
              })}
            </SectionHint>
          </div>
        </SectionHeader>
        <TreeWrap>
          <DebounceInput
            element={TextField}
            debounceTimeout={400}
            label={t("classStages.assignment.search")}
            variant="outlined"
            size="small"
            fullWidth
            value={searchText}
            onChange={event => setSearchText(event.target.value)}
          />
          {currentSeries && (
            <SeriesFilterRow>
              <FilterLabel component="span">
                <FilterListIcon fontSize="small" />
                {t("classStages.assignment.filters")}
              </FilterLabel>
              <FormControlLabel
                style={{ margin: 0 }}
                control={(
                  <Checkbox
                    color="primary"
                    size="small"
                    checked={sameSeriesOnly}
                    onChange={event => setSameSeriesOnly(event.target.checked)}
                  />
                )}
                label={(
                  <LeafLabel>
                    {t("classStages.assignment.sameSeriesOnly")}
                  </LeafLabel>
                )}
              />
            </SeriesFilterRow>
          )}
          {!loading && selectedIds.size === 0 && (
            <LinksRequiredWarning role="alert">
              <WarningIcon fontSize="small" />
              {t("classStages.assignment.linksRequired")}
            </LinksRequiredWarning>
          )}
          <TreePanel ref={panelRef} onScroll={handlePanelScroll}>
            {renderTree()}
          </TreePanel>
        </TreeWrap>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={onClose} disabled={confirming}>
          {t("common.actions.cancel")}
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleConfirm}
          disabled={confirming || selectedIds.size === 0}
          startIcon={
            confirming ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              undefined
            )
          }
        >
          {t("classStages.assignment.apply")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

StageAssignmentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  stageTitle: PropTypes.string,
  currentClassId: PropTypes.string,
  initialLinks: PropTypes.arrayOf(
    PropTypes.shape({
      idTurmaDisc: PropTypes.string.isRequired,
      classId: PropTypes.string,
      className: PropTypes.string,
      disciplineName: PropTypes.string
    })
  )
};

StageAssignmentDialog.defaultProps = {
  stageTitle: "",
  currentClassId: null,
  initialLinks: []
};

export default StageAssignmentDialog;
